import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './utils/theme';
import { isAuthenticated, getCurrentUser } from './utils/auth';

// Layout
import MainLayout from './components/layout/MainLayout';
import EmployeeLayout from './layouts/EmployeeLayout';

// Pages
import Home from './pages/Home';
import RentalListings from './pages/rentals/RentalListings';
import RoommateSearch from './pages/roommates/RoommateSearch';
import RentalDetail from './pages/rentals/RentalDetail';
import RoommateDetail from './pages/roommates/RoommateDetail';
import LegalResources from './pages/LegalResources';
import Furniture from './pages/Furniture';
import Videos from './pages/Videos';
import Auth from './pages/auth/Auth';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import UserProfile from './pages/profile/UserProfile';
import RoommatePostForm from './pages/posts/RoommatePostForm';
import LandlordProfile from './pages/profile/LandlordProfile';
import LandlordPostForm from './pages/posts/LandlordPostForm';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import EmployeeLogin from './pages/employee/EmployeeLogin';
import LandlordProgress from './pages/profile/LandlordProgress';
import StaffProgressOverview from './pages/employee/StaffProgressOverview';
import EmployeeProtectedRoute from './components/common/EmployeeProtectedRoute';
import LandlordComplete from './pages/profile/LandlordComplete';
import StaffComplete from './pages/employee/StaffComplete';
import LandlordContacts from './pages/profile/LandlordContacts';
import TermsPage from './pages/terms/TermsPage';
// 转租功能暂时禁用 - 保留代码供未来开发
// import SubletDetail from './pages/sublets/SubletDetail';
// import SubletPostForm from './pages/sublets/SubletPostForm';

// 保护路由组件
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const authenticated = isAuthenticated();
  if (!authenticated) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }
  return children;
};

// 房东路由保护组件
const LandlordRoute = ({ children }) => {
  const authenticated = isAuthenticated();
  const user = getCurrentUser();
  if (!authenticated || user?.user_type !== 'landlord') {
    return <Navigate to="/auth" />;
  }
  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* 员工端独立路由，完全不渲染主站Layout */}
          <Route path="/employee" element={<EmployeeLayout />}>
            <Route path="login" element={<EmployeeLogin />} />
            <Route
              path="progress-overview"
              element={
                <EmployeeProtectedRoute>
                  <StaffProgressOverview />
                </EmployeeProtectedRoute>
              }
            />
            <Route
              index
              element={
                <EmployeeProtectedRoute>
                  <EmployeeDashboard />
                </EmployeeProtectedRoute>
              }
            />
            <Route
              path="complete"
              element={
                <EmployeeProtectedRoute>
                  <StaffComplete />
                </EmployeeProtectedRoute>
              }
            />
          </Route>
          {/* 主站所有页面用MainLayout包裹 */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:uidb64/:token" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/" element={<MainLayout />}>
            {/* 房东路由 */}
            <Route
              path="landlord/profile"
              element={
                <LandlordRoute>
                  <LandlordProfile />
                </LandlordRoute>
              }
            />
            <Route
              path="landlord/listing/new"
              element={
                <LandlordRoute>
                  <LandlordPostForm />
                </LandlordRoute>
              }
            />
            <Route
              path="landlord/listing/edit/:id"
              element={
                <LandlordRoute>
                  <LandlordPostForm />
                </LandlordRoute>
              }
            />
            <Route
              path="landlord/progress"
              element={
                <LandlordRoute>
                  <LandlordProgress />
                </LandlordRoute>
              }
            />
            <Route
              path="landlord/complete"
              element={
                <LandlordRoute>
                  <LandlordComplete />
                </LandlordRoute>
              }
            />
            <Route
              path="landlord/contacts"
              element={
                <LandlordRoute>
                  <LandlordContacts />
                </LandlordRoute>
              }
            />
            {/* 租户路由 */}
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="posts/roommate/new"
              element={
                <ProtectedRoute>
                  <RoommatePostForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="posts/roommate/:id/edit"
              element={
                <ProtectedRoute>
                  <RoommatePostForm />
                </ProtectedRoute>
              }
            />
            {/* 转租功能暂时禁用 - 保留代码供未来开发
            <Route
              path="posts/sublet/new"
              element={
                <ProtectedRoute>
                  <SubletPostForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="posts/sublet/:id/edit"
              element={
                <ProtectedRoute>
                  <SubletPostForm />
                </ProtectedRoute>
              }
            />
            */}
            {/* 公共路由 */}
            <Route index element={<Home />} />
            <Route path="rentals" element={<RentalListings />} />
            <Route path="rentals/:slug" element={<RentalDetail />} />
            <Route path="roommates" element={<RoommateSearch />} />
            <Route path="roommates/:slug" element={<RoommateDetail />} />
            {/* 转租功能暂时禁用 - 保留代码供未来开发
            <Route path="sublets/:id" element={<SubletDetail />} />
            */}
            <Route path="legal" element={<LegalResources />} />
            <Route path="furniture" element={<Furniture />} />
            <Route path="videos" element={<Videos />} />
          </Route>
          {/* 主站路由 */}
          <Route path="/*" element={<MainLayout />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
